import { allSuburbProfiles } from '@/data/suburbProfilesData';

/**
 * Auto-links suburb names, service terms, and article topic keywords within markdown content.
 * Only links the FIRST occurrence of each term. Skips headings and existing links.
 * 
 * This is the core internal linking engine for SEO — every contextual link
 * strengthens the site's topical authority and helps search engines understand
 * content relationships.
 */

// Build suburb lookup: sorted longest-first so "South Brisbane" matches before "Brisbane"
const suburbEntries = allSuburbProfiles
  .map(p => ({ name: p.name, slug: p.slug }))
  .sort((a, b) => b.name.length - a.name.length);

// Service/page keywords to auto-link
const serviceLinks = [
  { term: 'development site', url: '/development-sites' },
  { term: 'development sites', url: '/development-sites' },
  { term: 'strategic advisory', url: '/services/strategic-advisory' },
  { term: 'project marketing', url: '/project-marketing' },
  { term: 'private transaction', url: '/private-transactions' },
  { term: 'private transactions', url: '/private-transactions' },
  { term: 'property valuation', url: '/valuation' },
  { term: 'free valuation', url: '/valuation' },
  { term: 'free property assessment', url: '/valuation' },
  { term: 'property advisory', url: '/services' },
  { term: 'contact ACRES', url: '/contact' },
  { term: 'residential sales', url: '/residential' },
  { term: 'residential listings', url: '/residential' },
  { term: 'current stock', url: '/stock' },
  { term: 'available stock', url: '/stock' },
  { term: 'our team', url: '/team' },
];

// Article topic keywords → link to the best article on that topic
// Sorted longest-first to match "site consolidation" before "site"
const articleLinks = [
  // Town planning & zoning
  { term: 'build a duplex', url: '/insights/can-i-build-duplex-on-my-property' },
  { term: 'duplex development', url: '/insights/can-i-build-duplex-on-my-property' },
  { term: 'dual occupancy', url: '/insights/can-i-build-duplex-on-my-property' },
  { term: 'granny flat', url: '/insights/can-i-build-granny-flat-my-property' },
  { term: 'secondary dwelling', url: '/insights/can-i-build-granny-flat-my-property' },
  { term: 'subdivide my block', url: '/insights/can-i-subdivide-my-block-brisbane' },
  { term: 'subdivision', url: '/insights/can-i-subdivide-my-block-brisbane' },
  { term: 'build townhouses', url: '/insights/can-i-build-townhouses-on-my-land' },
  { term: 'townhouse development', url: '/insights/can-i-build-townhouses-on-my-land' },
  { term: 'build units', url: '/insights/can-i-build-units-apartments-what-zoning' },
  { term: 'build apartments', url: '/insights/can-i-build-units-apartments-what-zoning' },

  // Zoning
  { term: 'low-medium density', url: '/insights/what-does-low-medium-density-zoning-mean' },
  { term: 'medium density residential', url: '/insights/medium-density-residential-explained' },
  { term: 'zoning map', url: '/insights/how-to-read-council-zoning-map-5-minutes' },
  { term: 'read a zoning map', url: '/insights/how-to-read-council-zoning-map-5-minutes' },
  { term: 'overlay', url: '/insights/what-is-overlay-how-affects-property-value' },
  { term: 'flood overlay', url: '/insights/what-is-overlay-how-affects-property-value' },
  { term: 'heritage overlay', url: '/insights/what-is-overlay-how-affects-property-value' },
  { term: 'character overlay', url: '/insights/what-is-overlay-how-affects-property-value' },
  { term: 'rezoning', url: '/insights/suburbs-being-rezoned-brisbane-2026' },
  { term: 'rezoned', url: '/insights/suburbs-being-rezoned-brisbane-2026' },

  // Infrastructure
  { term: 'Cross River Rail', url: '/insights/cross-river-rail-creating-development-sites' },
  { term: 'Brisbane Metro', url: '/insights/what-happens-property-values-new-train-station' },
  { term: '2032 Olympics', url: '/insights/how-2032-olympics-will-affect-brisbane-property-values' },
  { term: 'Brisbane Olympics', url: '/insights/how-2032-olympics-will-affect-brisbane-property-values' },
  { term: 'Olympic Games', url: '/insights/how-2032-olympics-will-affect-brisbane-property-values' },

  // Seller guides
  { term: 'sell to a developer', url: '/insights/should-i-sell-my-property-as-a-development-site' },
  { term: 'selling to a developer', url: '/insights/should-i-sell-my-property-as-a-development-site' },
  { term: 'what developers look for', url: '/insights/what-developers-look-for-when-buying-your-land' },
  { term: 'residual land value', url: '/insights/how-much-is-my-land-worth-for-development' },
  { term: 'land worth for development', url: '/insights/how-much-is-my-land-worth-for-development' },
  { term: 'how much is my land worth', url: '/insights/how-much-is-my-land-worth-for-development' },
  { term: 'maximise sale price', url: '/insights/how-to-maximise-sale-price-of-development-site' },
  { term: 'competitive tension', url: '/insights/how-to-maximise-sale-price-of-development-site' },
  { term: 'conditional contract', url: '/insights/conditional-contracts-explained-sellers-guide' },
  { term: 'conditional contracts', url: '/insights/conditional-contracts-explained-sellers-guide' },
  { term: 'put and call option', url: '/insights/what-is-a-put-and-call-option-sellers-guide' },
  { term: 'tax implications of selling', url: '/insights/tax-implications-selling-development-site-australia' },
  { term: 'capital gains tax', url: '/insights/capital-gains-tax-property-australia' },
  { term: 'DA approval', url: '/insights/should-i-get-development-approval-before-selling' },
  { term: 'development approval', url: '/insights/should-i-get-development-approval-before-selling' },
  { term: 'DA before selling', url: '/insights/should-i-get-development-approval-before-selling' },
  { term: 'off-market', url: '/insights/guide-to-selling-property-off-market-in-australia' },
  { term: 'off market', url: '/insights/guide-to-selling-property-off-market-in-australia' },
  { term: 'EOI campaign', url: '/insights/private-treaty-vs-eoi-vs-auction-development-sites' },
  { term: 'expressions of interest', url: '/insights/private-treaty-vs-eoi-vs-auction-development-sites' },

  // Site consolidation
  { term: 'site consolidation', url: '/insights/what-is-site-consolidation-could-it-benefit-you' },
  { term: 'site amalgamation', url: '/insights/sell-with-neighbour-site-amalgamation' },
  { term: 'sell with your neighbour', url: '/insights/selling-together-neighbour-higher-price' },
  { term: 'selling together', url: '/insights/selling-together-neighbour-higher-price' },

  // Landowner guides
  { term: 'large block', url: '/insights/how-much-is-large-block-worth-brisbane' },
  { term: 'big block', url: '/insights/why-big-backyard-worth-more-than-house' },
  { term: 'big backyard', url: '/insights/why-big-backyard-worth-more-than-house' },
  { term: 'corner block', url: '/insights/corner-block-why-developers-pay-premium' },
  { term: 'splitter block', url: '/insights/splitter-block-guide-queensland' },
  { term: 'acreage subdivision', url: '/insights/acreage-subdivision-rural-land-urban-gold' },
  { term: 'inherited property', url: '/insights/inherited-property-development-site' },
  { term: 'downsizing', url: '/insights/downsizing-selling-to-developer-fund-retirement' },

  // Residential seller
  { term: 'how much is my house worth', url: '/insights/how-much-is-my-house-worth-brisbane' },
  { term: 'best real estate agent', url: '/insights/best-real-estate-agent-brisbane' },
  { term: 'sell a house fast', url: '/insights/how-to-sell-house-fast-brisbane' },
  { term: 'selling costs', url: '/insights/selling-costs-calculator-queensland' },
  { term: 'agent commissions', url: '/insights/real-estate-agent-commissions-queensland' },
  { term: 'auction vs private', url: '/insights/auction-vs-private-sale-brisbane' },
  { term: 'settlement day', url: '/insights/what-happens-on-settlement-day-sellers-guide' },
  { term: 'deceased estate', url: '/insights/deceased-estate-sale-guide' },

  // Market & comparison
  { term: 'Brisbane property market', url: '/insights/brisbane-property-market-outlook-2025-2026' },
  { term: 'property market outlook', url: '/insights/brisbane-property-market-outlook-2025-2026' },
  { term: 'code assessable', url: '/insights/development-application-vs-code-assessable' },
  { term: 'development application', url: '/insights/development-application-vs-code-assessable' },
].sort((a, b) => b.term.length - a.term.length);


/**
 * Processes markdown text and injects internal links.
 * Returns { content, mentionedSuburbs }
 */
export function autoLinkContent(markdown, currentArticleId = '') {
  if (!markdown) return { content: '', mentionedSuburbs: [] };

  const linked = new Set();
  const mentionedSuburbs = [];

  // Process line by line to skip headings
  const lines = markdown.split('\n');
  const processed = lines.map(line => {
    // Skip headings, images, and existing markdown links
    if (line.startsWith('#') || line.startsWith('![')) return line;

    let modified = line;

    // Auto-link article topic keywords (first occurrence only)
    for (const { term, url } of articleLinks) {
      // Skip if this article links to itself
      if (currentArticleId && url.includes(currentArticleId)) continue;

      const key = `art:${url}`;
      if (linked.has(key)) continue;

      const regex = new RegExp(`(?<!\\[)\\b(${escapeRegex(term)})\\b(?![^\\[]*\\])`, 'gi');
      if (regex.test(modified)) {
        let replaced = false;
        modified = modified.replace(regex, (match) => {
          if (replaced) return match;
          replaced = true;
          linked.add(key);
          return `[${match}](${url})`;
        });
      }
    }

    // Auto-link suburbs (first occurrence only)
    for (const { name, slug } of suburbEntries) {
      if (linked.has(slug)) continue;

      const regex = new RegExp(`(?<!\\[)\\b(${escapeRegex(name)})\\b(?![^\\[]*\\])`, 'g');
      if (regex.test(modified)) {
        let replaced = false;
        modified = modified.replace(regex, (match) => {
          if (replaced) return match;
          replaced = true;
          linked.add(slug);
          mentionedSuburbs.push({ name, slug });
          return `[${match}](/suburb/${slug})`;
        });
      }
    }

    // Auto-link service terms (first occurrence only, case-insensitive)
    for (const { term, url } of serviceLinks) {
      const key = `svc:${url}`;
      if (linked.has(key)) continue;

      const regex = new RegExp(`(?<!\\[)\\b(${escapeRegex(term)})\\b(?![^\\[]*\\])`, 'gi');
      if (regex.test(modified)) {
        let replaced = false;
        modified = modified.replace(regex, (match) => {
          if (replaced) return match;
          replaced = true;
          linked.add(key);
          return `[${match}](${url})`;
        });
      }
    }

    return modified;
  });

  return {
    content: processed.join('\n'),
    mentionedSuburbs,
  };
}

/**
 * Finds related articles based on shared suburb mentions, category, and content overlap.
 * Uses a scoring system: category match = 2pts, each shared suburb = 3pts, keyword overlap = 1pt.
 */
export function findRelatedBySuburbs(currentId, currentSuburbs, allArticles, limit = 4) {
  const suburbNames = new Set((currentSuburbs || []).map(s => s.name.toLowerCase()));

  // Get current article for category matching
  const current = allArticles.find(a => a.id === currentId);
  const currentCategory = current?.category?.toLowerCase() || '';

  const scored = allArticles
    .filter(a => a.id !== currentId)
    .map(a => {
      let score = 0;

      // Category match: +2
      if (currentCategory && (a.category || '').toLowerCase() === currentCategory) {
        score += 2;
      }

      // Suburb overlap: +3 per shared suburb
      if (suburbNames.size > 0) {
        const content = (a.content || '').toLowerCase();
        for (const name of suburbNames) {
          if (content.includes(name)) score += 3;
        }
      }

      // Tag/keyword overlap: +1 per shared tag
      if (current?.tags && a.tags) {
        const currentTags = new Set(current.tags.map(t => t.toLowerCase()));
        for (const tag of a.tags) {
          if (currentTags.has(tag.toLowerCase())) score += 1;
        }
      }

      return { article: a, score };
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map(s => s.article);
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
